import { AccessTime, CalendarToday, InfoOutline, LowPriority, TaskAlt } from '@mui/icons-material';
import { DateTime } from 'luxon';
import React from 'react';

import { ITaskInfo } from '@/entities/task/model/client';
import { colorStore } from '@/features/queueColors';
import { WeeklyWorkLog } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getTrackerUrl } from '@/utils/getTrackerUrl';

export type FieldType = 'time' | 'date' | 'text' | 'link' | 'taskLink';

export type FieldConfig = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  type: FieldType;
  getValue: (log: WeeklyWorkLog, task: ITaskInfo | null) => React.ReactNode | null;
  group?: 'time' | 'general';
};

export const getFieldsConfig = (): FieldConfig[] => [
  // Временные поля
  {
    id: 'duration',
    label: 'Затрачено времени',
    icon: <AccessTime />,
    type: 'time',
    group: 'time',
    getValue: (log) => log && formatDurationRussian(log.durationMinutes),
  },
  {
    id: 'estimation',
    label: 'Общая оценка задачи',
    icon: <AccessTime />,
    type: 'time',
    group: 'time',
    getValue: (_log, task) =>
      task?.key && task.estimation ? formatDurationRussian(task.estimation) : null,
  },
  {
    id: 'spent',
    label: 'Затрачено всего времени',
    icon: <AccessTime />,
    type: 'time',
    group: 'time',
    getValue: (_log, task) => (task?.key && task.spent ? formatDurationRussian(task.spent) : null),
  },
  // Остальные поля
  {
    id: 'priority',
    label: 'Приоритет',
    icon: <LowPriority />,
    type: 'text',
    getValue: (_log, task) =>
      task?.key && task?.priority?.display?.length > 0 ? task.priority.display : null,
  },
  {
    id: 'logDate',
    label: 'Дата лога',
    icon: <CalendarToday />,
    type: 'date',
    getValue: (log) => log && DateTime.fromISO(log.date).setLocale('ru').toFormat('dd MMMM yyyy'),
  },
  {
    id: 'createdAt',
    label: 'Дата создания задачи',
    icon: <CalendarToday />,
    type: 'date',
    getValue: (_log, task) =>
      task?.key && task?.createdAt?.length > 0
        ? DateTime.fromISO(task.createdAt).setLocale('ru').toFormat('dd MMMM yyyy')
        : null,
  },
  {
    id: 'parent',
    label: 'Родительский тикет',
    icon: <TaskAlt />,
    type: 'taskLink',
    getValue: (_log, task) => {
      if (!task?.key || !task?.parent?.key || !task.parent?.display?.length) return null;

      const parentPrefix = task.parent.key.split('-')[0];
      const parentColor = colorStore.getColorByPrefix(parentPrefix);
      const needsBorder = colorStore.needsBorder(parentPrefix);

      return (
        <a
          onClick={(e) => e.stopPropagation()}
          href={getTrackerUrl(task.parent.key)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: parentColor,
            ...(needsBorder && {
              textShadow: '0 0 1px rgba(0, 0, 0, 0.5), 0 0 1px rgba(0, 0, 0, 0.5)',
            }),
          }}
        >
          {task.parent.display}
        </a>
      );
    },
  },
  {
    id: 'status',
    label: 'Статус задачи',
    icon: <InfoOutline />,
    type: 'text',
    getValue: (_log, task) =>
      task?.key && task?.status?.display?.length > 0 ? task.status.display : null,
  },
];
